import React, { useState } from 'react';
import Modal from './Modal';
import { useAuth, changePassword, updateDisplayName, resetPassword, signOut } from '../AuthContext';
import { useToast } from './Toast';

function friendlyError(err) {
  const code = err?.code || '';
  if (code.includes('wrong-password') || code.includes('invalid-credential') || code.includes('invalid-login-credentials')) {
    return 'Current password is incorrect.';
  }
  if (code.includes('weak-password')) return 'New password must be at least 6 characters.';
  if (code.includes('too-many-requests')) return 'Too many attempts — please wait a minute and try again.';
  if (code.includes('network-request-failed')) return 'Network error — check your connection and try again.';
  return 'Something went wrong — please try again.';
}

export default function AccountModal({ onClose }) {
  const { user, refreshUser } = useAuth();
  const toast = useToast();

  const [name, setName] = useState(user?.displayName || '');
  const [savingName, setSavingName] = useState(false);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwError, setPwError] = useState(null);
  const [savingPw, setSavingPw] = useState(false);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await updateDisplayName(name.trim());
      refreshUser();
      toast('Name updated.', 'ok');
    } catch (err) {
      toast(friendlyError(err), 'danger');
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    if (next !== confirm) {
      setPwError('New passwords do not match.');
      return;
    }
    setSavingPw(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      toast('Password changed.', 'ok');
      onClose();
    } catch (err) {
      setPwError(friendlyError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const sendReset = async () => {
    try {
      await resetPassword(user.email);
      toast(`Reset link sent to ${user.email}.`, 'ok');
    } catch (err) {
      toast(friendlyError(err), 'danger');
    }
  };

  return (
    <Modal title="Account" onClose={onClose} maxWidth={440}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-6)' }}>
        <div className="cs-field">
          <label>Email</label>
          <input className="cs-input" value={user?.email || ''} disabled />
        </div>

        <form className="u-stack" style={{ '--gap': 'var(--sp-3)' }} onSubmit={saveName}>
          <div className="cs-field">
            <label htmlFor="acct-name">Display name</label>
            <input
              id="acct-name"
              className="cs-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="How teammates see you"
              maxLength={80}
            />
          </div>
          <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="cs-btn cs-btn-outline cs-btn-sm" disabled={savingName || (name.trim() === (user?.displayName || ''))}>
              {savingName ? 'Saving…' : 'Save name'}
            </button>
          </div>
        </form>

        <form className="u-stack" style={{ '--gap': 'var(--sp-3)' }} onSubmit={savePassword}>
          <strong className="u-small">Change password</strong>
          {pwError && (
            <div className="cs-empty" style={{ padding: 12, color: 'var(--danger)' }}>{pwError}</div>
          )}
          <div className="cs-field">
            <label htmlFor="acct-pw-current">Current password</label>
            <input
              id="acct-pw-current"
              type="password"
              className="cs-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="cs-field">
            <label htmlFor="acct-pw-new">New password</label>
            <input
              id="acct-pw-new"
              type="password"
              className="cs-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <div className="cs-field">
            <label htmlFor="acct-pw-confirm">Confirm new password</label>
            <input
              id="acct-pw-confirm"
              type="password"
              className="cs-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <div className="u-split" style={{ alignItems: 'center' }}>
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={sendReset}>
              Forgot it? Email me a reset link
            </button>
            <button type="submit" className="cs-btn cs-btn-primary" disabled={savingPw || !current || !next || !confirm}>
              {savingPw ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>

        <div className="u-split" style={{ alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 'var(--sp-4)' }}>
          <span className="u-small u-muted">Signed in as {user?.email}</span>
          <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      </div>
    </Modal>
  );
}
